import type { GetMessagesCallbackResponse } from 'core_main/sdk/get_messages/types';
import { createOptimisticMessage } from 'widget_main/screens/MessageThread/helpers';
import { getKustomerCore } from 'widget_main/globals/helpers';
import { MessageCallbackResponse } from 'core_main/sdk/send_message/types';
import type {
  FetchMessageHistoryHelperOptions,
  FetchMessageHistoryHelperResponse,
  MessageHistoryEntity,
} from '../types';
import * as assistantHelpers from './assistant';

const kustomerCore = getKustomerCore();

export const selectOldestMessage = (options: MessageHistoryEntity) => {
  const { messages, messagesById } = options;
  const oldestMessageId = messages[0];
  return messagesById?.[oldestMessageId];
};

export const mergeAndSortByMessageTimestamps = (
  previousMessages: Record<string, MessageCallbackResponse>,
  newMessages: Record<string, MessageCallbackResponse>,
) => {
  const messagesById = {
    ...previousMessages,
  };
  Object.assign(messagesById, newMessages);
  const messages = Object.keys(messagesById)
    .slice()
    .sort((firstId, secondId) => {
      const firstMsg = messagesById[firstId];
      const secondMsg = messagesById[secondId];

      const firstDate = new Date(firstMsg.createdAt);
      const secondDate = new Date(secondMsg.createdAt);
      return firstDate < secondDate ? -1 : 1;
    });

  const oldestMessage = selectOldestMessage({
    messagesById,
    messages,
  });
  const hasAllMessages =
    oldestMessage?.directionType?.startsWith('initial-') || false;

  return {
    messagesById,
    messages,
    hasAllMessages,
  };
};

const constructAssistedConversationWithOptimisticMessageError = (
  conversation,
) => {
  const { conversationId } = conversation;
  return assistantHelpers.getActiveAssistant().then((initAssistantPayload) => {
    if (initAssistantPayload) {
      const optimisticMessage = createOptimisticMessage({
        conversationId,
        body: conversation.preview,
        isInAssistantMode: true,
        initAssistantPayload,
      });
      optimisticMessage.showError = true;
      return {
        initialMessages: initAssistantPayload.initialMessages,
        optimisticMessages: [optimisticMessage],
      };
    }
    const optimisticMessage = createOptimisticMessage({
      conversationId,
      body: conversation.preview,
      isInAssistantMode: false,
    });
    optimisticMessage.showError = true;
    return {
      optimisticMessages: [optimisticMessage],
    };
  });
};

const fetchHistoricalMessages = (
  options: FetchMessageHistoryHelperOptions,
): Promise<FetchMessageHistoryHelperResponse> => {
  const { conversation, messagesById } = options;
  const { conversationId } = conversation;
  const oldestMessage = selectOldestMessage({
    messages: conversation.messages,
    messagesById,
  });
  const before = oldestMessage?.createdAt;
  return new Promise((resolve, reject) => {
    kustomerCore.getHistoricalMessages(
      { conversationId, before },
      (response: GetMessagesCallbackResponse, error) => {
        if (error) {
          reject(error);
          return;
        }
        const NewMessagesById = response.messages.reduce((acc, val) => {
          acc[val.messageId] = acc[val.messageId] || val;
          return acc;
        }, {});
        const merged = mergeAndSortByMessageTimestamps(
          messagesById,
          NewMessagesById,
        );
        if (merged.messages?.length === 0) {
          const initialMessageMissingOnAssistedConversation =
            conversation?.isInAssistantMode &&
            !conversation?.initialMessages?.length;
          const emptyMessageState = {
            messages: [],
            messagesById: {},
            hasAllMessages: true,
          };
          if (initialMessageMissingOnAssistedConversation) {
            constructAssistedConversationWithOptimisticMessageError(
              conversation,
            ).then((assistedConversationWithOptimisticMessage) => {
              resolve({
                ...emptyMessageState,
                ...assistedConversationWithOptimisticMessage,
              });
            });
          } else {
            resolve(emptyMessageState);
          }
        } else {
          resolve({
            ...merged,
            satisfaction: response.satisfaction?.timetoken
              ? response.satisfaction
              : conversation.satisfaction,
            hasAllMessages:
              merged.hasAllMessages || response.messages.length === 0,
          });
        }
      },
    );
  });
};

const fetchRecentMessages = (
  options: FetchMessageHistoryHelperOptions,
): Promise<FetchMessageHistoryHelperResponse> => {
  const { conversation, messagesById } = options;
  const { conversationId } = conversation;
  return new Promise((resolve, reject) => {
    kustomerCore.getMessages(
      { conversationId },
      (response: GetMessagesCallbackResponse, error) => {
        if (error) {
          reject(error);
          return;
        }
        const NewMessagesById = response.messages.reduce((acc, val) => {
          acc[val.messageId] = acc[val.messageId] || val;
          return acc;
        }, {});
        const merged = mergeAndSortByMessageTimestamps(
          messagesById,
          NewMessagesById,
        );
        if (merged.hasAllMessages) {
          resolve({
            ...merged,
            satisfaction: response.satisfaction,
          });
        } else {
          resolve(
            fetchHistoricalMessages({
              conversation: {
                ...conversation,
                ...merged,
                satisfaction: response.satisfaction,
              },
              messagesById: merged.messagesById,
            }),
          );
        }
      },
    );
  });
};

/*

Flowchart showing stages of fetching messages for a conversation
https://miro.com/app/board/uXjVOXmVapo=/

*/
export const fetchMessageHistoryHelper = (
  options: FetchMessageHistoryHelperOptions,
): Promise<FetchMessageHistoryHelperResponse> => {
  const { conversation, messagesById } = options;
  const oldestMessage = selectOldestMessage({
    messages: conversation.messages,
    messagesById,
  });
  if (oldestMessage?.source === 'historical') {
    return fetchHistoricalMessages(options);
  }
  return fetchRecentMessages(options);
};
